import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Get API URL with validation
const getAPIUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (!url) {
    console.error('REACT_APP_BACKEND_URL environment variable is not configured');
    return null;
  }
  return url;
};

const API_URL = getAPIUrl();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if API_URL is available
  useEffect(() => {
    if (!API_URL) {
      setError('Backend URL not configured. Please check environment variables.');
      setIsLoading(false);
    }
  }, []);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const storedAuth = localStorage.getItem('swati_auth');
    if (storedAuth) {
      try {
        const authData = JSON.parse(storedAuth);
        if (authData.token && authData.user) {
          // Verify token with backend
          verifyToken(authData.token).then(valid => {
            if (valid) {
              setIsAuthenticated(true);
              setUser(authData.user);
            } else {
              // Token invalid, clear storage
              localStorage.removeItem('swati_auth');
            }
            setIsLoading(false);
          });
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error parsing auth data:', error);
        localStorage.removeItem('swati_auth');
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [verifyToken]);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsAuthenticated(true);
        setUser(data.user);
        
        // Store token and user data in localStorage
        localStorage.setItem('swati_auth', JSON.stringify({
          token: data.token,
          user: data.user
        }));
        
        return { success: true };
      } else {
        return { success: false, message: data.detail || data.message || 'Invalid username or password' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      const storedAuth = localStorage.getItem('swati_auth');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        // Call backend logout endpoint (optional - token invalidation happens client-side)
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authData.token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('swati_auth');
    }
  };

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
    error
  };

  // Show error screen if API URL is not configured
  if (error && !API_URL) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '500px',
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Configuration Error</h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>{error}</p>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            Please contact your system administrator.
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
